import { PenLine, Presentation, X } from 'lucide-react';
import { Suspense, lazy } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { BoardStatus, BoardSync } from './collab';
import type { BoardSyncElement } from './collab';
import type { BoardPointerBatch } from './CursorLayer';

/** The heavy editor chunk loads on first board mount; the panel shell renders instantly. */
const ExcalidrawBoard = lazy(() => import('./ExcalidrawBoard'));

export interface BoardPanelProps {
  /** The conference room slug the board belongs to (header chip only). */
  readonly roomId: string;
  /** True when the local user may draw: role admin/moderator or a board grant (ADR 0013). */
  readonly isEditor: boolean;
  /** Connection health of the room's realtime channel - the board rides it (ADR 0013). */
  readonly status: BoardStatus;
  /** Sends one incremental board_update with the changed element snapshots. */
  readonly sendUpdate: (elements: readonly BoardSyncElement[]) => void;
  /** Subscribes to the room's remote board syncs (deltas and persisted scenes). */
  readonly subscribeSync: (listener: (sync: BoardSync) => void) => () => void;
  /** Sends the local cursor position in scene coordinates (CR-18; editors only server-side). */
  readonly sendPointer: (x: number, y: number) => void;
  /** Subscribes to the room's coalesced remote-cursor batches (CR-18). */
  readonly subscribePointers: (listener: (batch: BoardPointerBatch) => void) => () => void;
  /** Resolves an identity's display name for a cursor label. */
  readonly labelFor: (identity: string) => string;
  /** 'surface' renders the webinar board surface; 'overlay' the meeting panel with chrome. */
  readonly variant: 'surface' | 'overlay';
  /** Overlay only: closes the panel. */
  readonly onClose?: () => void;
  readonly className?: string | undefined;
}

const STATUS_DOT: Readonly<Record<BoardStatus, string>> = {
  connecting: 'bg-amber-400',
  live: 'bg-emerald-400',
  reconnecting: 'bg-amber-400',
  closed: 'bg-zinc-500',
};

/**
 * The Доска panel: a slim header (title, transport health, read-only mark, close) around the
 * lazy collaborative canvas. Used as the webinar rooms' board surface (ADR 0012) and as
 * the meeting rooms' Доска overlay.
 */
export function BoardPanel({
  roomId,
  isEditor,
  status,
  sendUpdate,
  subscribeSync,
  sendPointer,
  subscribePointers,
  labelFor,
  variant,
  onClose,
  className,
}: BoardPanelProps): JSX.Element {
  const { t } = useTranslation();
  const statusLabel = t(`board.status_${status}`);

  return (
    <section
      data-testid="board-panel"
      aria-label={t('board.title')}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-xl border border-white/[0.08] bg-zinc-950',
        className,
      )}
    >
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-white/[0.06] px-3">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.05] px-2 py-0.5 text-[11px] font-semibold tracking-wide text-zinc-300">
          <Presentation className="size-3.5 text-emerald-400" />
          {t('board.title')}
        </span>
        <span
          data-testid="board-status"
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500"
          title={statusLabel}
        >
          <span aria-hidden className={cn('size-1.5 rounded-full', STATUS_DOT[status])} />
          {statusLabel}
        </span>
        {!isEditor ? (
          <span
            data-testid="board-readonly-badge"
            className="inline-flex items-center gap-1 rounded-md bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-zinc-400"
            title={t('board.readonly')}
          >
            <PenLine className="size-3" aria-hidden />
            {t('board.readonly')}
          </span>
        ) : null}
        {variant === 'overlay' ? (
          <span className="ml-auto flex items-center gap-2">
            <span className="hidden font-mono text-[10px] text-zinc-600 sm:inline">/{roomId}</span>
            <button
              type="button"
              data-testid="board-close"
              aria-label={t('board.close_aria')}
              title={t('board.close_aria')}
              onClick={onClose}
              className="grid size-6 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="size-3.5" />
            </button>
          </span>
        ) : null}
      </div>
      <div className="relative min-h-0 flex-1">
        <Suspense
          fallback={
            <p role="status" className="grid h-full place-items-center text-sm text-zinc-500">
              {t('board.loading')}
            </p>
          }
        >
          <ExcalidrawBoard
            isEditor={isEditor}
            sendUpdate={sendUpdate}
            subscribeSync={subscribeSync}
            sendPointer={sendPointer}
            subscribePointers={subscribePointers}
            labelFor={labelFor}
          />
        </Suspense>
      </div>
    </section>
  );
}
